import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState, ThunkApi } from 'state/store';
import { Request, RequestStatusPatchPayload } from 'types/store/normalized';
import { Status, SortingType } from 'utils/customTypes';
import { RequestStatus } from 'utils/types/request';
import { SLICE_STATUS } from 'utils/constants';
import {
  DEFAULT_REQUEST_STATUS_FILTER,
  REQUEST_STATUSES,
} from 'utils/constants/request';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/ui';
import { RangeFilter } from 'utils/types/filters';

/* ============================== STATE SETUP =============================== */
type FilterParameterType = {
  status?: readonly RequestStatus[];
  filterParams?: {
    requesterIds?: string[];
    projectIds?: string[];
    ownerIds?: string[];
    businessUnitIds?: string[];
    numberOfProjects?: string[];
    ldPriority?: string[];
    formIds?: string[];
    updatedAt?: RangeFilter;
    createdAt?: RangeFilter;
    submittedAt?: RangeFilter;
    decisionDate?: RangeFilter;
    myRequests?: boolean;
    archived?: boolean;
  };
};

/**
 * CGP-221: the exact {status, filterParams, search} the list was last
 * SUCCESSFULLY fetched with, so a "select all" export can send the very same
 * filters the backend used to build the visible list. The thunk returns them
 * in payload.appliedParams and the fulfilled reducer records them — recording
 * on pending would let a rejected fetch overwrite the params of the rows
 * still on screen, and reading the live search parameter would leak a search
 * typed while a fetch was in flight (the concurrency condition drops it).
 */
interface LastAppliedFetchParams {
  status: RequestStatus[];
  filterParams?: FilterParameterType['filterParams'];
  search: string;
}

/**
 * Filters object of the export-requests POST body (CGP-221 backend contract).
 * Empty/unset fields are omitted entirely, mirroring how the list query
 * params drop them (`|| undefined`).
 */
export interface ExportRequestsFilters {
  statusList?: string[];
  search?: string;
  requesterIds?: string[];
  ownerIds?: string[];
  projectIds?: string[];
  businessUnitIds?: string[];
  ldPriority?: string[];
  numberOfProjects?: string[];
  formIds?: string[];
  updatedAt?: Partial<RangeFilter>;
  createdAt?: Partial<RangeFilter>;
  submittedAt?: Partial<RangeFilter>;
  decisionDate?: Partial<RangeFilter>;
  myRequests?: boolean;
  archived?: boolean;
}

interface RequestsState {
  status: Status;
  limit: number;
  links: { next?: string; last?: string };
  total: number;
  cursor?: string;
  searchParameter: string;
  filterParameter: FilterParameterType;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  isFetchingNext: boolean;
  /** Request id of the last successfully patched request; set on fulfilled */
  lastPatchedRequestId: string | null;
  /** CGP-221: parameters of the last dispatched list fetch; null before one */
  lastAppliedFetchParams: LastAppliedFetchParams | null;
}

const initialState: RequestsState = {
  status: SLICE_STATUS.IDLE,
  limit: DEFAULT_PAGE_SIZE,
  links: { next: undefined, last: undefined },
  total: 0,
  searchParameter: '',
  filterParameter: {},
  isFetchingNext: false,
  lastPatchedRequestId: null,
  lastAppliedFetchParams: null,
};

const requestsAdapter = createEntityAdapter<Request>();

/* ============================== REDUX THUNK =============================== */
function concurrencyCondition(
  thunkParameters: unknown,
  { getState }: { getState: () => RootState }
): boolean {
  const { status } = getState().normalized.requests;
  return status !== SLICE_STATUS.LOADING;
}

interface GetListParams {
  pageSize?: number;
  status?: readonly RequestStatus[];
  fetchNext?: boolean;
  filterParams?: {
    requesterIds?: string[];
    projectIds?: string[];
    ownerIds?: string[];
    businessUnitIds?: string[];
    numberOfProjects?: string[];
    ldPriority?: string[];
    formIds?: string[];
    updatedAt?: RangeFilter;
    createdAt?: RangeFilter;
    submittedAt?: RangeFilter;
    decisionDate?: RangeFilter;
    myRequests?: boolean;
    archived?: boolean;
  };
}
export const getRequests = createAsyncThunk<
  {
    data: Request[];
    totalCount: number;
    links: { next?: string };
    cursor?: string;
    /** CGP-221: the parameters this fetch actually applied (see above) */
    appliedParams: LastAppliedFetchParams;
  },
  GetListParams,
  ThunkApi
>(
  'normalized/requests/getRequests',
  async (
    {
      pageSize = DEFAULT_PAGE_SIZE,
      // Never default to a list containing COMPLETED: a backend without the
      // CGP-178 enum 400s the entire request. Flag-aware call sites opt in.
      status = DEFAULT_REQUEST_STATUS_FILTER,
      fetchNext = false,
      filterParams,
    },
    { extra: { requestsApi }, getState }
  ) => {
    const state = getState();
    const search = state.normalized.requests.searchParameter;
    const sortBy = state.normalized.requests.sortBy;
    const sortOrder = state.normalized.requests.sortOrder;
    const cursor = fetchNext ? state.normalized.requests.cursor : undefined;

    // CGP-268: an explicitly-empty status list means the caller's filters
    // intersect to no fetchable statuses. The query builder below would drop
    // filter[status] entirely (''.join → '' → undefined) and collapse the
    // request into an unfiltered one — which used to leak other users'
    // private drafts. Resolve with an empty page instead of hitting the API
    // so EVERY call site (mount, column sort, side panel, unarchive, future
    // consumers) is safe by construction.
    if (status.length === 0) {
      return {
        data: [],
        totalCount: 0,
        cursor: undefined,
        links: {},
        appliedParams: { status: [], filterParams, search },
      };
    }

    const params = {
      'filter[businessUnitIds]':
        filterParams?.businessUnitIds?.join(',') || undefined,
      'filter[requesterIds]':
        filterParams?.requesterIds?.join(',') || undefined,
      'filter[projectIds]': filterParams?.projectIds?.join(',') || undefined,
      'filter[ownerIds]': filterParams?.ownerIds?.join(',') || undefined,
      'filter[status]': status?.join(',') || undefined,
      'filter[ldPriority]': filterParams?.ldPriority?.join(',') || undefined,
      'filter[numberOfProjects]':
        filterParams?.numberOfProjects?.join(',') || undefined,
      'filter[formIds]': filterParams?.formIds?.join(',') || undefined,
      'filter[search]': search || undefined,
      'filter[updatedAt][from]': filterParams?.updatedAt?.from || undefined,
      'filter[updatedAt][to]': filterParams?.updatedAt?.to || undefined,
      'filter[createdAt][from]': filterParams?.createdAt?.from || undefined,
      'filter[createdAt][to]': filterParams?.createdAt?.to || undefined,
      'filter[submittedAt][from]': filterParams?.submittedAt?.from || undefined,
      'filter[submittedAt][to]': filterParams?.submittedAt?.to || undefined,
      'filter[decisionDate][from]':
        filterParams?.decisionDate?.from || undefined,
      'filter[decisionDate][to]': filterParams?.decisionDate?.to || undefined,
      'filter[myRequests]': filterParams?.myRequests || undefined,
      'filter[archived]': filterParams?.archived || undefined,
      after: cursor,
      pageSize,
      sortBy: sortBy || undefined,
      sortOrder: sortBy ? sortOrder : undefined,
    };

    const { data, meta, links } = await requestsApi().getList({
      params,
    });
    return {
      data,
      totalCount: meta.totalCount,
      cursor: meta.cursor,
      links,
      // copy: `status` may alias the readonly DEFAULT_REQUEST_STATUS_FILTER
      appliedParams: { status: [...status], filterParams, search },
    };
  },
  { condition: concurrencyCondition }
);

export const deleteRequest = createAsyncThunk<string, string, ThunkApi>(
  'normalized/requests/deleteRequest',
  async (requestId, { extra: { requestsApi } }) => {
    await requestsApi().deleteRequest(requestId);
    return requestId;
  }
);

export const patchRequestStatus = createAsyncThunk<
  Request,
  { requestId: string; payload: RequestStatusPatchPayload },
  ThunkApi
>(
  'normalized/requests/patchRequestStatus',
  async (
    { requestId, payload },
    { extra: { requestsApi }, rejectWithValue }
  ) => {
    try {
      return await requestsApi().patchRequest(requestId, payload);
    } catch (error) {
      // Forward the v2 API's `{ error }` body so the caller can show the real
      // reason (e.g. "Only advanced requests can be marked as completed." when
      // a legacy request is completed) instead of a generic failure toast.
      if (axios.isAxiosError(error)) {
        return rejectWithValue(error.response?.data);
      }
      return rejectWithValue(undefined);
    }
  }
);

/**
 * Extracts the human-readable message from a rejected API payload. The v2 API
 * returns validation errors as `{ error: string }`, which patchRequestStatus
 * forwards via rejectWithValue (so `.unwrap()` rejects with it). Returns
 * undefined when there is no usable string, letting callers fall back to a
 * generic message.
 */
export const getApiErrorMessage = (error: unknown): string | undefined => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'error' in error &&
    typeof (error as { error: unknown }).error === 'string'
  ) {
    return (error as { error: string }).error;
  }
  return undefined;
};

/** Modal params from RequestStatusModals onConfirmAction */
export interface RequestStatusModalParams {
  comments?: string;
  conditions?: string[];
  details?: string;
  reason?: string;
  message?: string;
  // CGP-253: Actual Completion Date (ISO 8601) supplied by CompletedModal.
  completedAt?: string;
}

/**
 * Builds a minimal PATCH body for an inline status change. Do NOT add
 * request-level `data` fields here (published, baseFormId, decisionDate,
 * additionalInformation, ...): the v2 backend replaces the stored `data`
 * blob with whatever the client sends on a status patch, so resending
 * fabricated or stale fields overwrites server-owned state (CGP-71 wiped
 * the Internal Information Comments field this way). Plain transitions omit
 * `data` entirely and the backend preserves stored data and stamps
 * decisionDate/submittedAt itself. Modal transitions always send their own
 * subobject — with empty defaults when the modal collected nothing — so a
 * re-transition replaces any stale subobject from a previous transition
 * instead of letting it survive the backend merge (CGP-89). Preserving
 * stored fields OUTSIDE the transition's subobject is the backend's job.
 *
 * CANCELED is the one exception: `cancellation` is built across two
 * transitions — the requester stamps `reason`/`details` on
 * PENDING_CANCELLATION, then L&D adds `ldRemark` on CANCELED. The backend
 * shallow-merges the cancel payload's `data` over the stored `data`, so it
 * replaces the whole `cancellation` subobject; we must therefore re-send the
 * stored `reason`/`details` (passed via params) alongside `ldRemark` or they
 * are wiped, breaking CancelledRequestBanner (CGP-88).
 */
export function buildRequestStatusPatchPayload(
  newStatus: RequestStatus,
  params?: RequestStatusModalParams
): RequestStatusPatchPayload {
  switch (newStatus) {
    case REQUEST_STATUSES.APPROVED:
      return {
        status: newStatus,
        data: { approval: { comments: params?.comments ?? '' } },
      };
    case REQUEST_STATUSES.WAITLISTED:
      return {
        status: newStatus,
        data: {
          waitlisted: {
            conditions: params?.conditions ?? [],
            comments: params?.comments ?? '',
            details: params?.details ?? '',
          },
        },
      };
    case REQUEST_STATUSES.REJECTED:
      return {
        status: newStatus,
        data: {
          declination: {
            reason: params?.reason ?? '',
            details: params?.message ?? '',
            comments: params?.comments ?? '',
          },
        },
      };
    case REQUEST_STATUSES.CANCELED:
      return {
        status: newStatus,
        data: {
          cancellation: {
            ...(params?.reason != null && { reason: params.reason }),
            ...(params?.details != null && { details: params.details }),
            ldRemark: params?.comments ?? '',
          },
        },
      };
    case REQUEST_STATUSES.COMPLETED:
      // CGP-253: send the editable Actual Completion Date alongside the
      // comment. Only include `completedAt` when present so the backend
      // (CGP-256) can fall back to `now`; it must stay inside `completed`
      // because the status PATCH replaces the whole `data` blob.
      return {
        status: newStatus,
        data: {
          completed: {
            comments: params?.comments ?? '',
            ...(params?.completedAt != null && {
              completedAt: params.completedAt,
            }),
          },
        },
      };
    default:
      return { status: newStatus };
  }
}

/* ================================= REDUCER ================================ */
const requestsSlice = createSlice({
  name: 'normalized/requests',
  initialState: requestsAdapter.getInitialState(initialState),
  reducers: {
    setSearchParameter: (state, action: PayloadAction<string>) => {
      state.ids = [];
      state.searchParameter = action.payload;
    },
    setSortParameter: (
      state,
      action: PayloadAction<{
        sortBy: string;
        sortOrder: SortingType;
      }>
    ) => {
      state.sortBy = action.payload.sortBy;
      state.sortOrder = action.payload.sortOrder;
    },
    setIsFetchingNext: (state, action: PayloadAction<boolean>) => {
      state.isFetchingNext = action.payload;
    },
    setFilterParameter: (state, action: PayloadAction<FilterParameterType>) => {
      Object.assign(state.filterParameter, action.payload);
    },
    resetStatus: (state) => {
      state.status = SLICE_STATUS.IDLE;
    },
    resetRequests: (state) => {
      state.ids = [];
    },
    clearLastPatchedRequestId: (state) => {
      state.lastPatchedRequestId = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRequests.pending, (state) => {
        state.status = SLICE_STATUS.LOADING;
      })
      .addCase(getRequests.rejected, (state) => {
        state.status = SLICE_STATUS.FAILED;
      })
      .addCase(getRequests.fulfilled, (state, action) => {
        const isFetchingNext = action.meta.arg.fetchNext;

        if (!isFetchingNext) {
          requestsAdapter.setAll(state, action.payload.data);
          state.ids = action.payload.data.map((request) => request.id);
        } else {
          requestsAdapter.upsertMany(state, action.payload.data);
          state.ids = Array.from(
            new Set([
              ...state.ids,
              ...action.payload.data.map((request) => request.id),
            ])
          );
        }

        if (action.meta.arg.pageSize) {
          state.limit = action.meta.arg.pageSize;
        }
        state.links.last = state.links.next;
        state.links.next = action.payload.links.next;

        state.total = action.payload.totalCount;
        state.cursor = action.payload.cursor;

        // CGP-221: record the parameters this fetch actually applied so a
        // "select all" export reuses exactly what built the visible list
        state.lastAppliedFetchParams = action.payload.appliedParams;

        state.status = SLICE_STATUS.SUCCESS;
      })
      .addCase(deleteRequest.fulfilled, (state, action) => {
        requestsAdapter.removeOne(state, action.payload);
        state.total -= 1;
      })
      .addCase(deleteRequest.rejected, () => {
        throw new Error();
      })
      .addCase(patchRequestStatus.fulfilled, (state, action) => {
        state.lastPatchedRequestId = action.payload.id;
        const existingRequest = state.entities[action.payload.id];
        requestsAdapter.updateOne(state, {
          id: action.payload.id,
          changes: {
            ...action.payload,
            ownerIds:
              action.payload.ownerIds?.length ||
              !existingRequest?.ownerIds?.length
                ? action.payload.ownerIds
                : existingRequest.ownerIds,
          },
        });
      });
  },
});

/* ================================ ACTIONS ================================= */
export const { setSearchParameter } = requestsSlice.actions;
export const { resetStatus } = requestsSlice.actions;
export const { setFilterParameter } = requestsSlice.actions;
export const { resetRequests } = requestsSlice.actions;
export const { setSortParameter } = requestsSlice.actions;
export const { setIsFetchingNext } = requestsSlice.actions;
export const { clearLastPatchedRequestId } = requestsSlice.actions;

/* =============================== SELECTORS ================================ */
const defaultSelectors = requestsAdapter.getSelectors<RootState>(
  (state) => state.normalized.requests
);

const selectCanFetchMore = (state: RootState) =>
  !!state.normalized.requests.links.next;

const selectTotalRequests = (state: RootState) =>
  state.normalized.requests.total;

const selectLoadedRequests = (state: RootState) =>
  state.normalized.requests.ids.length;

const selectStatus = (state: RootState) => state.normalized.requests.status;

const selectSearchParameter = (state: RootState) =>
  state.normalized.requests.searchParameter;

/**
 * INVARIANT: must stay memoized. Returning a fresh object literal per call makes
 * `useSelector`'s reference comparison fail on every dispatched action, which
 * re-renders the whole requests table and (via its `useLayoutEffect` on this
 * value) forces a `scrollTo` layout write on every action.
 */
const selectSortParameter = createSelector(
  (state: RootState) => state.normalized.requests.sortBy,
  (state: RootState) => state.normalized.requests.sortOrder,
  (sortBy, sortOrder) => ({ sortBy, sortOrder })
);

const selectIsFetchingNext = (state: RootState) =>
  state.normalized.requests.isFetchingNext;

const selectFilterParameter = (state: RootState) =>
  state.normalized.requests.filterParameter;

const selectLastPatchedRequestId = (state: RootState) =>
  state.normalized.requests.lastPatchedRequestId;

const selectLastAppliedFetchParams = (state: RootState) =>
  state.normalized.requests.lastAppliedFetchParams;

/** Copies a readonly string array, dropping it entirely when empty/unset. */
const toNonEmptyArray = (values?: readonly string[]): string[] | undefined =>
  values && values.length > 0 ? [...values] : undefined;

/** Keeps only the truthy halves of a range, dropping empty ranges entirely. */
const toNonEmptyRange = (
  range?: RangeFilter
): Partial<RangeFilter> | undefined => {
  if (!range?.from && !range?.to) {
    return undefined;
  }
  return {
    ...(range?.from && { from: range.from }),
    ...(range?.to && { to: range.to }),
  };
};

/**
 * CGP-221: maps the parameters of the last SUCCESSFUL list fetch — including
 * the search that fetch actually used, never the live input — onto the export
 * API's `filters` contract. Empty/unset fields are omitted entirely — the
 * same `|| undefined` semantics the list query params use — so the backend
 * applies exactly the filters the visible list did.
 */
const selectExportFilters = createSelector(
  [selectLastAppliedFetchParams],
  (lastApplied): ExportRequestsFilters => {
    const filterParams = lastApplied?.filterParams;
    const candidate: ExportRequestsFilters = {
      statusList: toNonEmptyArray(lastApplied?.status),
      search: lastApplied?.search || undefined,
      requesterIds: toNonEmptyArray(filterParams?.requesterIds),
      ownerIds: toNonEmptyArray(filterParams?.ownerIds),
      projectIds: toNonEmptyArray(filterParams?.projectIds),
      businessUnitIds: toNonEmptyArray(filterParams?.businessUnitIds),
      ldPriority: toNonEmptyArray(filterParams?.ldPriority),
      numberOfProjects: toNonEmptyArray(filterParams?.numberOfProjects),
      formIds: toNonEmptyArray(filterParams?.formIds),
      updatedAt: toNonEmptyRange(filterParams?.updatedAt),
      createdAt: toNonEmptyRange(filterParams?.createdAt),
      submittedAt: toNonEmptyRange(filterParams?.submittedAt),
      decisionDate: toNonEmptyRange(filterParams?.decisionDate),
      myRequests: filterParams?.myRequests || undefined,
      archived: filterParams?.archived || undefined,
    };
    return Object.fromEntries(
      Object.entries(candidate).filter(([, value]) => value !== undefined)
    ) as ExportRequestsFilters;
  }
);

export const requestsSelectors = {
  ...defaultSelectors,
  selectTotalRequests,
  selectLoadedRequests,
  selectCanFetchMore,
  selectStatus,
  selectSearchParameter,
  selectFilterParameter,
  selectSortParameter,
  selectIsFetchingNext,
  selectLastPatchedRequestId,
  selectExportFilters,
};

export default requestsSlice.reducer;
